<template>
  <Transition name="alert">
    <div v-if="show" :class="alertClasses" role="alert">
      <!-- Icon -->
      <div v-if="showIcon" class="flex-shrink-0">
        <slot name="icon">
          <svg v-if="variant === 'success'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="variant === 'danger'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <svg v-else-if="variant === 'warning'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        </slot>
      </div>

      <!-- Content -->
      <div class="flex-1 ml-3">
        <h4 v-if="title" :class="titleClasses">{{ title }}</h4>
        <p :class="messageClasses">
          <slot>{{ message }}</slot>
        </p>
        <!-- Actions -->
        <div v-if="$slots.actions" class="mt-3">
          <slot name="actions"></slot>
        </div>
      </div>

      <!-- Close button -->
      <button
        v-if="closable"
        type="button"
        class="flex-shrink-0 ml-auto -mr-1.5 -mt-1.5 p-1.5 rounded-lg focus:outline-none focus:ring-2 transition-colors"
        :class="closeButtonClasses"
        @click="handleClose"
      >
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'BaseAlert',
  props: {
    show: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'info',
      validator: (value) => ['info', 'success', 'warning', 'danger'].includes(value)
    },
    title: {
      type: String,
      default: null
    },
    message: {
      type: String,
      default: null
    },
    closable: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    rounded: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    outlined: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  computed: {
    alertClasses() {
      return [
        'flex items-start transition-all duration-300',
        this.sizeClasses,
        this.variantClasses,
        this.roundedClasses
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'p-3 text-sm',
        md: 'p-4 text-base',
        lg: 'p-5 text-lg'
      }
      return sizes[this.size]
    },
    variantClasses() {
      if (this.outlined) {
        const outlinedVariants = {
          info: 'bg-white border-2 border-blue-500 text-blue-700',
          success: 'bg-white border-2 border-green-500 text-green-700',
          warning: 'bg-white border-2 border-yellow-500 text-yellow-700',
          danger: 'bg-white border-2 border-red-500 text-red-700'
        }
        return outlinedVariants[this.variant]
      }

      const variants = {
        info: 'bg-blue-50 border border-blue-200 text-blue-800',
        success: 'bg-green-50 border border-green-200 text-green-800',
        warning: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
        danger: 'bg-red-50 border border-red-200 text-red-800'
      }
      return variants[this.variant]
    },
    roundedClasses() {
      const rounded = {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl'
      }
      return rounded[this.rounded]
    },
    titleClasses() {
      const classes = {
        info: 'font-semibold text-blue-900 mb-1',
        success: 'font-semibold text-green-900 mb-1',
        warning: 'font-semibold text-yellow-900 mb-1',
        danger: 'font-semibold text-red-900 mb-1'
      }
      return classes[this.variant]
    },
    messageClasses() {
      const classes = {
        info: 'text-blue-700',
        success: 'text-green-700',
        warning: 'text-yellow-700',
        danger: 'text-red-700'
      }
      return classes[this.variant]
    },
    closeButtonClasses() {
      const classes = {
        info: 'text-blue-500 hover:bg-blue-100 focus:ring-blue-300',
        success: 'text-green-500 hover:bg-green-100 focus:ring-green-300',
        warning: 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-300',
        danger: 'text-red-500 hover:bg-red-100 focus:ring-red-300'
      }
      return classes[this.variant]
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.alert-enter-active,
.alert-leave-active {
  transition: all 0.3s ease;
}

.alert-enter-from,
.alert-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
